import React, { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import {
  Alert, AlertDescription, AlertIcon, Box, Button, ButtonGroup, Checkbox, Drawer, DrawerBody, DrawerCloseButton, DrawerContent, DrawerFooter, DrawerHeader,
  DrawerOverlay, Flex, FormControl, FormHelperText, FormLabel, HStack, Input, Link as CLink, Modal, ModalBody, ModalCloseButton, ModalContent, ModalFooter,
  ModalHeader, ModalOverlay, Table, TableContainer, Tbody, Td, Text, Textarea, Th, Thead, Tr, VStack, useToast,
} from '@chakra-ui/react'
import { Check, Copy, Phone, UserPlus } from 'lucide-react'
import { applications, type PartnerRequest, type RequestStatus } from '@services/operators'
import { apiErrorMessage, formatDate, timeAgo } from '@utils/format'
import { ConfirmModal, EmptyState, Label, PageHeader, Panel, Pill, SkeletonRows, tableHead } from '@components/ui'

const STEPS: Array<{ key: RequestStatus; label: string; hint: string; next?: { to: RequestStatus; label: string } }> = [
  { key: 'new', label: 'New', hint: 'Call them and explain how joining works.', next: { to: 'contacted', label: 'I have called them' } },
  { key: 'contacted', label: 'Contacted', hint: 'Ask them to request their documents from LATRA.', next: { to: 'documents_requested', label: 'They are getting their documents' } },
  { key: 'documents_requested', label: 'Documents requested', hint: 'Waiting for them to bring the LATRA licence, company papers and the signed agreement.', next: { to: 'documents_received', label: 'Documents received' } },
  { key: 'documents_received', label: 'Documents received', hint: 'Check the LATRA licence and the signed agreement, then create the account.' },
]
const TABS: Array<{ key: string; label: string }> = [
  { key: 'open', label: 'Open' }, ...STEPS.map(s => ({ key: s.key, label: s.label })), { key: 'closed', label: 'Closed' },
]
const label = (s: RequestStatus) => STEPS.find(x => x.key === s)?.label ?? (s === 'approved' ? 'Account created' : 'Declined')
const tone = (s: RequestStatus) => (s === 'new' ? 'orange' : s === 'approved' ? 'brand' : s === 'declined' ? 'gray' : 'blue') as any

function Credentials({ data, onClose }: { data: { ownerEmail: string; temporaryPassword: string } | null; onClose: () => void }) {
  const toast = useToast()
  const [copied, setCopied] = useState(false)
  const text = data ? `Daladala Smart partner portal\nSign in: ${data.ownerEmail}\nTemporary password: ${data.temporaryPassword}` : ''
  return (
    <Modal isOpen={!!data} onClose={onClose} isCentered closeOnOverlayClick={false}>
      <ModalOverlay />
      <ModalContent borderRadius="2xl">
        <ModalHeader>Account created</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Alert status="warning" borderRadius="xl" fontSize="sm" mb={4}>
            <AlertIcon />
            <AlertDescription>This password is shown <b>once</b>. Give it to the owner by phone or in person, and ask them to change it after signing in.</AlertDescription>
          </Alert>
          <VStack align="stretch" spacing={3}>
            <Box><Label>Sign in with</Label><Text fontWeight="semibold">{data?.ownerEmail}</Text></Box>
            <Box><Label>Temporary password</Label><Text fontFamily="mono" fontSize="lg" fontWeight="semibold" letterSpacing="0.04em">{data?.temporaryPassword}</Text></Box>
            <Box><Label>Where</Label><Text fontSize="sm">The partner portal on the Daladala Smart website, under Partners, Sign in.</Text></Box>
          </VStack>
        </ModalBody>
        <ModalFooter gap={2}>
          <Button leftIcon={copied ? <Check size={16} /> : <Copy size={16} />} variant="outline"
            onClick={async () => { try { await navigator.clipboard.writeText(text); setCopied(true) } catch { toast({ title: 'Could not copy. Select the text and copy it.', status: 'warning' }) } }}>
            {copied ? 'Copied' : 'Copy details'}
          </Button>
          <Button onClick={onClose}>Done</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}

function Detail({ id, onClose, onCreated }: { id: string; onClose: () => void; onCreated: (c: { ownerEmail: string; temporaryPassword: string }) => void }) {
  const qc = useQueryClient()
  const toast = useToast()
  const { data: r } = useQuery(['partner-requests', 'one', id], () => applications.get(id).then(res => res.data))
  const [notes, setNotes] = useState('')
  const [licence, setLicence] = useState('')
  const [agreement, setAgreement] = useState(false)
  const [declining, setDeclining] = useState(false)
  useEffect(() => { if (r) { setNotes(r.staffNotes ?? ''); setLicence(r.latraLicenceNo ?? ''); setAgreement(!!r.agreementSignedAt) } }, [r?.id, r?.updatedAt]) // eslint-disable-line react-hooks/exhaustive-deps

  const refresh = () => { qc.invalidateQueries('partner-requests'); qc.invalidateQueries('control-home') }
  const save = useMutation((body: Parameters<typeof applications.update>[1]) => applications.update(id, body), {
    onSuccess: () => { toast({ title: 'Saved', status: 'success', duration: 1500 }); refresh() },
    onError: e => { toast({ title: apiErrorMessage(e), status: 'error' }) },
  })
  const approve = useMutation(() => applications.approve(id, { latraLicenceNo: licence.trim(), agreementSigned: agreement }), {
    onSuccess: res => { refresh(); onCreated(res.data); onClose() },
    onError: e => { toast({ title: apiErrorMessage(e), status: 'error' }) },
  })
  const decline = useMutation((reason: string) => applications.decline(id, reason), {
    onSuccess: () => { toast({ title: 'Request declined', status: 'success' }); setDeclining(false); refresh(); onClose() },
    onError: e => { toast({ title: apiErrorMessage(e), status: 'error' }) },
  })

  const step = STEPS.find(s => s.key === r?.status)
  const idx = STEPS.findIndex(s => s.key === r?.status)
  const closed = r?.status === 'approved' || r?.status === 'declined'
  const canCreate = r?.status === 'documents_received' && !!licence.trim() && agreement

  return (
    <Drawer isOpen onClose={onClose} size="md" placement="right">
      <DrawerOverlay />
      <DrawerContent>
        <DrawerCloseButton />
        <DrawerHeader pb={2}>
          {r?.companyName ?? 'Request'}
          {r && <Box mt={1}><Pill tone={tone(r.status)}>{label(r.status)}</Pill></Box>}
        </DrawerHeader>
        <DrawerBody>
          {!r ? <SkeletonRows rows={4} /> : (
            <VStack align="stretch" spacing={5}>
              <Box>
                <Label>Contact</Label>
                <Text fontWeight="semibold">{r.contactName}</Text>
                <HStack spacing={4} mt={1} fontSize="sm" wrap="wrap">
                  <CLink href={`tel:${r.phone}`} color="brand.600" display="inline-flex" alignItems="center" gap={1}><Phone size={14} />{r.phone}</CLink>
                  <CLink href={`mailto:${r.email}`} color="brand.600">{r.email}</CLink>
                </HStack>
              </Box>
              <Flex gap={6} wrap="wrap">
                <Box><Label>Buses</Label><Text>{r.busCount ?? '—'}</Text></Box>
                <Box><Label>Region</Label><Text>{r.region || '—'}</Text></Box>
                <Box><Label>Received</Label><Text>{formatDate(r.createdAt)}</Text></Box>
              </Flex>
              {r.routesInterest && <Box><Label>Routes they are interested in</Label><Text fontSize="sm" whiteSpace="pre-wrap">{r.routesInterest}</Text></Box>}
              {r.message && <Box><Label>Their message</Label><Text fontSize="sm" whiteSpace="pre-wrap">{r.message}</Text></Box>}

              {closed ? (
                <Alert status={r.status === 'approved' ? 'success' : 'info'} borderRadius="xl" fontSize="sm">
                  <AlertIcon />
                  <AlertDescription>
                    {r.status === 'approved' ? <>Account created. LATRA licence <b>{r.latraLicenceNo}</b>, agreement signed {formatDate(r.agreementSignedAt)}.</> : <>Declined: {r.declineReason}</>}
                  </AlertDescription>
                </Alert>
              ) : (
                <>
                  <Box>
                    <Label>Where this request is</Label>
                    <HStack spacing={1} mt={2}>
                      {STEPS.map((s, i) => <Box key={s.key} flex={1} h="6px" borderRadius="full" bg={i <= idx ? 'brand.500' : 'gray.200'} />)}
                    </HStack>
                    <Text mt={2} fontSize="sm" color="gray.600"><b>{step?.label}.</b> {step?.hint}</Text>
                    {step?.next && (
                      <Button mt={3} size="sm" leftIcon={<Check size={14} />} isLoading={save.isLoading} onClick={() => save.mutate({ status: step.next!.to })}>{step.next.label}</Button>
                    )}
                  </Box>

                  <FormControl>
                    <FormLabel fontSize="sm">Notes (only staff see this)</FormLabel>
                    <Textarea value={notes} onChange={e => setNotes(e.target.value)} rows={3} maxLength={4000} placeholder="What did they say? What was agreed?" />
                    <Button mt={2} size="xs" variant="outline" isDisabled={notes === (r.staffNotes ?? '')} isLoading={save.isLoading} onClick={() => save.mutate({ staffNotes: notes })}>Save notes</Button>
                  </FormControl>

                  <Box borderTop="1px solid" borderColor="gray.100" pt={4}>
                    <Label>Before creating the account</Label>
                    <FormControl mt={3}>
                      <FormLabel fontSize="sm">Their LATRA licence number</FormLabel>
                      <Input value={licence} onChange={e => setLicence(e.target.value)} maxLength={60} placeholder="As printed on their own licence" />
                      <FormHelperText>Buses run under the company's own licence, so check the original before entering it.</FormHelperText>
                    </FormControl>
                    <Checkbox mt={3} isChecked={agreement} onChange={e => setAgreement(e.target.checked)}>The signed partner agreement is on file</Checkbox>
                    {r.status !== 'documents_received' && <Text mt={3} fontSize="xs" color="gray.500">You can create the account once the documents are marked as received.</Text>}
                  </Box>
                </>
              )}
            </VStack>
          )}
        </DrawerBody>
        {r && !closed && (
          <DrawerFooter gap={2} borderTop="1px solid" borderColor="gray.100" flexWrap="wrap">
            <Button variant="ghost" colorScheme="red" onClick={() => setDeclining(true)}>Decline</Button>
            <Button leftIcon={<UserPlus size={16} />} isDisabled={!canCreate} isLoading={approve.isLoading} onClick={() => approve.mutate()}>Create company account</Button>
          </DrawerFooter>
        )}
      </DrawerContent>
      <ConfirmModal
        isOpen={declining} onClose={() => setDeclining(false)} loading={decline.isLoading} tone="red" title="Decline this request" confirmLabel="Decline"
        body={<Text>Say why, so the decision can be explained later. {r?.companyName} can send a new request.</Text>}
        reasonLabel="Reason" reasonRequired onConfirm={({ reason }) => decline.mutate(reason)}
      />
    </Drawer>
  )
}

export default function PartnerRequestsPage() {
  const [params, setParams] = useSearchParams()
  const tab = params.get('status') || 'open'
  const open = params.get('open')
  const [q, setQ] = useState('')
  const [created, setCreated] = useState<{ ownerEmail: string; temporaryPassword: string } | null>(null)
  const { data, isLoading } = useQuery(['partner-requests', tab, q], () => applications.list({ status: tab, q: q || undefined }).then(r => r.data), { refetchInterval: 30_000, keepPreviousData: true })
  const items = data?.items ?? []
  const counts = data?.counts ?? {}
  const openCount = (counts.new ?? 0) + (counts.contacted ?? 0) + (counts.documents_requested ?? 0) + (counts.documents_received ?? 0)
  const setTab = (key: string) => { const p = new URLSearchParams(params); p.set('status', key); p.delete('open'); setParams(p, { replace: true }) }
  const select = (id: string | null) => { const p = new URLSearchParams(params); if (id) p.set('open', id); else p.delete('open'); setParams(p, { replace: true }) }

  return (
    <>
      <PageHeader
        eyebrow="Partners" title="Partner requests"
        subtitle="Bus companies that asked to join. They have no sign-in until you have met them, checked their own LATRA licence and received the signed agreement."
      />
      <Box overflowX="auto" mb={4}>
        <ButtonGroup isAttached size="sm" variant="outline">
          {TABS.map(t => {
            const n = t.key === 'open' ? openCount : t.key === 'closed' ? (counts.approved ?? 0) + (counts.declined ?? 0) : counts[t.key as RequestStatus] ?? 0
            return <Button key={t.key} isActive={tab === t.key} bg={tab === t.key ? 'brand.50' : undefined} onClick={() => setTab(t.key)} flexShrink={0}>{t.label}{n ? ` · ${n}` : ''}</Button>
          })}
        </ButtonGroup>
      </Box>
      <Input mb={4} maxW="sm" bg="white" placeholder="Search company, person, phone or email" value={q} onChange={e => setQ(e.target.value)} />

      <Panel flush p={0}>
        {isLoading ? <SkeletonRows /> : !items.length ? (
          <EmptyState icon={<UserPlus size={26} />} title={tab === 'open' ? 'No open requests' : 'Nothing here'} body="When a company fills in the Become a partner form on the website, it appears here and in your Control Center." />
        ) : (
          <TableContainer>
            <Table>
              <Thead><Tr><Th {...tableHead}>Company</Th><Th {...tableHead}>Contact</Th><Th {...tableHead} isNumeric>Buses</Th><Th {...tableHead}>Step</Th><Th {...tableHead}>Received</Th></Tr></Thead>
              <Tbody>
                {items.map((i: PartnerRequest) => (
                  <Tr key={i.id} cursor="pointer" _hover={{ bg: 'gray.50' }} onClick={() => select(i.id)}>
                    <Td borderColor="gray.100"><Text fontWeight="medium" fontSize="sm">{i.companyName}</Text>{i.region && <Text fontSize="xs" color="gray.500">{i.region}</Text>}</Td>
                    <Td borderColor="gray.100" fontSize="sm"><Text>{i.contactName}</Text><Text fontSize="xs" color="gray.500">{i.phone}</Text></Td>
                    <Td borderColor="gray.100" isNumeric>{i.busCount ?? '—'}</Td>
                    <Td borderColor="gray.100"><Pill tone={tone(i.status)}>{label(i.status)}</Pill></Td>
                    <Td borderColor="gray.100" fontSize="sm">{timeAgo(i.createdAt)}</Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </TableContainer>
        )}
      </Panel>

      {open && <Detail id={open} onClose={() => select(null)} onCreated={setCreated} />}
      <Credentials data={created} onClose={() => setCreated(null)} />
    </>
  )
}
